/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import {
  Box,
  useResponsiveWrapperContext,
  ResponsiveBox,
} from '@usap/uicomponents'
import {
  Vselector,
  OverlaySearchContext,
} from '@usap/widgets'
import { useFeatureFlagCookieContext } from '@usap/core'
import GTM from '../../../helpers/GTM'
import Image from './ProductCard/Image'
import Title from './ProductCard/Title'
import PartNo from './ProductCard/PartNo'
import ReviewsRatings from './ProductCard/ReviewsRatings'
import PricingInfo from './ProductCard/PricingInfo'
import ShippingExpInfo from './ProductCard/ShippingExpInfo'
import MoreAttributes from './ProductCard/MoreAttributes'
import { SearchContext } from '../../common/context/SearchContextProvider'
import useVehicleController from '../../../controllers/vehicle.controller'
import HoverBox from './style/ProductCard.css'

import AddButtonForPL from '../../ShoppingCartV2/components/AddToCart/AddButtonForPL'
import PageTypeIdentifier from '../../../helpers/PageTypeIdentifier'
import useToast from '../../DIFM/Toast/useToast'
import errorConstants from '../../ShoppingCartV2/constants'
import useIsMobile from '../../DIFM/DifmSelect/useIsMobile'

const style = {
  hoverBox: { side: 'bottom', size: 'xsmall', color: 'light-3' },
  productImageWidth: { large: '100%', other: '40%' },
  direction: { large: 'column', other: 'row' },
  gap: { large: 'small', other: 'medium' },
  wrapWidth: { large: '100%', other: '60%' },
}

const vehicleKeys = ['year', 'make', 'model']
const trueValue = true

export default function ProductCard(props) {
  const {
    cardId,
    index,
    data,
    onClickNotify,
    invDetails,
    cityName,
    zipCode,
    zipCodeHandler,
    zipcodeError,
    isLoading,
    getDeliveryDate,
    removePrice,
    isShowHeight,
    pageType,
    boxWidth,
    coreConfig,
    isLocationUpdated,
    setIsLocationUpdated
  } = props

  const {
    searchState: { products },
  } = useContext(SearchContext)

  const ref = useRef()

  const { newFitmentFlag } = useFeatureFlagCookieContext()

  const { updateVehicles } = useVehicleController()

  const {
    selectedVehicle = {},
    isVehicleFit = '',
  } = products
  const {
    id: invId,
    sku,
    brand,
    skuTitle,
    productUri,
    productImageUrl,
    isUniversal,
    averageRating = 0,
    reviewCount = 0,
    pricing,
    fitNotes = [],
    productAttributes = [],
    seRequired,
    hasFitNote,
  } = data

  const { isDesktop } = useResponsiveWrapperContext()
  const [groundDeliveryTime, setGroundDeliveryTime] = useState()
  const invObj = invDetails[`${invId}`]
  const skipVehicleLink = productUri.includes('/details')
  const overlayDetails = useContext(OverlaySearchContext)
  const overlayDetailsInvId = overlayDetails && overlayDetails[`${invId}`]
  const productImage = overlayDetailsInvId || productImageUrl

  const hasValidVehicle = () => {
    const selectedVehicleKeys = Object.keys(selectedVehicle)
    const selectedVehicleRefine = vehicleKeys.filter(key => selectedVehicleKeys.includes(key))
    const isValidVehicle = selectedVehicleRefine.length === vehicleKeys.length
    return [isValidVehicle, isValidVehicle && isVehicleFit]
  }
  const [valid, isValidVehicle] = hasValidVehicle()

  const trackProductClick = useCallback((product, currentIndex) => {
    const { productClicks } = GTM

    productClicks(
      pageType === 'mntsrp' ? 'Maintenance Recommendations' : 'Category and Search Product Listing',
      {
        ...product,
        vehicle: selectedVehicle,
        inventory: invObj,
        groundDeliveryTime,
      },
      currentIndex,
    )
  }, [groundDeliveryTime, invObj, pageType, selectedVehicle])

  const searchVehicleTrigger = useCallback(() => updateVehicles({
    showVehicleSelector: true,
    overlay: true,
    changeVehicleAction: true,
  }), [updateVehicles])

  const trackProductClickFn = useCallback(() => {
    PageTypeIdentifier.pdp()
    trackProductClick(data, index)
  }, [data, index, trackProductClick])

  const expandFitment = newFitmentFlag && hasFitNote && !seRequired

  const isMobile = useIsMobile()
  const toastTestId = isMobile ? errorConstants.TOAST_TEST_ID_MOBILE : errorConstants.TOAST_TEST_ID_DESKTOP
  const toast = useToast({ parentRef: ref, autoCloseDelay: errorConstants.TOAST_AUTO_CLOSE_DELAY, testId: toastTestId })
  const handleOnAddToCartError = useCallback(() => {
    toast.info({ content: errorConstants.ERROR_MESSAGE })
  }, [toast])

  const ProductImage = useMemo(() => (
    <ResponsiveBox fxWidth={style.productImageWidth}>
      <Image
        productIndex={index}
        productImage={productImage}
        productUri={productUri}
        skipVehicleLink={skipVehicleLink}
        imgTitle={skuTitle}
        className="lazyload"
        sku={sku}
        brand={brand}
        index={index}
        onClick={trackProductClickFn}
      />
    </ResponsiveBox>
  ), [
    index,
    productImage,
    productUri,
    skipVehicleLink,
    skuTitle,
    sku,
    brand,
    trackProductClickFn,
  ])

  const ProductTitle = useMemo(() => (
    <Title
      sku={sku}
      title={skuTitle}
      brand={brand}
      link={productUri}
      skip={skipVehicleLink}
      index={index}
      onClick={trackProductClickFn}
    />
  ), [brand, index, productUri, skipVehicleLink, sku, skuTitle, trackProductClickFn])

  const PartNumber = useMemo(() => (
    <PartNo sku={sku} />
  ), [sku])

  const PartReviews = useMemo(() => (
    <ReviewsRatings
      id={invId}
      averageRating={averageRating}
      reviewCount={reviewCount}
    />
  ), [averageRating, invId, reviewCount])

  const VehicleFitment = useMemo(() => {
    const selectedVehicleData = valid ? selectedVehicle : {}

    return (
      <Box direction="row" gap="small">
        <Vselector
          universalFit={isUniversal}
          vehicleFit={isValidVehicle}
          searchVehicleTrigger={searchVehicleTrigger}
          selectedVehicle={selectedVehicleData}
          isCard={trueValue}
          getProductDetails={data}
          hideBorder={trueValue}
        />
      </Box>
    )
  }, [data, isUniversal, isValidVehicle, searchVehicleTrigger, selectedVehicle, valid])

  const ProductPriceInfo = useMemo(() => (
    <PricingInfo
      index={index}
      pricing={pricing}
      removePrice={removePrice}
      invObj={invObj}
      coreConfig={coreConfig}
    />
  ), [coreConfig, index, invObj, pricing, removePrice])

  const ShippingInfo = useMemo(() => (
    <ShippingExpInfo
      setGroundDeliveryTime={setGroundDeliveryTime}
      index={index}
      data={data}
      invDetails={invDetails}
      cityName={cityName}
      zipCode={zipCode}
      zipCodeHandler={zipCodeHandler}
      zipcodeError={zipcodeError}
      isLoading={isLoading}
      getDeliveryDate={getDeliveryDate}
      isShowHeight={isShowHeight}
      pageType={pageType}
      isLocationUpdated={isLocationUpdated}
      setIsLocationUpdated={setIsLocationUpdated}
    />
  ), [
    cityName,
    data,
    getDeliveryDate,
    index,
    invDetails,
    isLoading,
    isShowHeight,
    pageType,
    zipCode,
    zipCodeHandler,
    zipcodeError,
  ])

  const AddToCart = useMemo(
    () => (
      <AddButtonForPL
        cardId={cardId}
        onClickNotify={onClickNotify}
        index={index}
        data={data}
        invObj={invObj}
        groundDeliveryTime={groundDeliveryTime}
        pageType={pageType}
        onError={handleOnAddToCartError}
      />
    ),
    [cardId, onClickNotify, index, data, invObj, groundDeliveryTime, pageType, handleOnAddToCartError],
  )

  const FitNotes = useMemo(() => (
    <MoreAttributes
      fitNotes={fitNotes}
      productAttributes={productAttributes}
      index={index}
      sku={sku}
      expandFitment={expandFitment}
    />
  ), [expandFitment, fitNotes, index, productAttributes, sku])

  return (
    <HoverBox
      id={cardId}
      boxWidth={boxWidth}
      boxMargin={isDesktop ? 8 : 0}
      border={style.hoverBox}
      ref={ref}
    >
      <Box pad="medium">
        <ResponsiveBox
          fxDirection={style.direction}
          fxGap={style.gap}
          wrap={isDesktop}
        >
          {ProductImage}
          <ResponsiveBox fxWidth={style.wrapWidth}>
            {ProductTitle}
            {PartNumber}
            {PartReviews}
            {VehicleFitment}
            {ProductPriceInfo}
            {ShippingInfo}
            {AddToCart}
            {FitNotes}
          </ResponsiveBox>
        </ResponsiveBox>
      </Box>
    </HoverBox>
  )
}
